import { hrmsGet, hrmsPost, hrmsPut } from "../api/api";
import {
  CLAIM_TYPE,
  EXPENSE_CATEGORY,
  isOwnVehicleMode,
} from "../modules/org-local-conveyance/constants/conveyanceConstants";


const CODES = {
  SEARCH_POLICY: "HR29S",
  ADD_POLICY: "HR29A",
  UPDATE_POLICY: "HR29U",
  SEARCH_MODE: "HR30S",
  ADD_MODE: "HR30A",
  UPDATE_MODE: "HR30U",
};

/** "" -> null, so empty numeric inputs clear the column instead of failing. */
function decimal(value) {
  return value === "" || value === null || value === undefined ? null : Number(value);
}

/** Fields the hidden section owns are nulled rather than sent stale. */
function policyPayload(policy) {
  const isRatePerKm = policy.claimType === CLAIM_TYPE.RATE_PER_KM;
  const maintained = Boolean(policy.maintenanceSupported);
  return {
    category: EXPENSE_CATEGORY,
    claimType: policy.claimType,
    fixedMonthlyAmount: isRatePerKm ? null : decimal(policy.fixedMonthlyAmount),
    tollsParkingCovered: Boolean(policy.tollsParkingCovered),
    maintenanceSupported: maintained,
    maintenanceMaxBike: maintained ? decimal(policy.maintenanceMaxBike) : null,
    maintenanceMaxCar: maintained ? decimal(policy.maintenanceMaxCar) : null,
    maintenanceFrequency: maintained ? policy.maintenanceFrequency : null,
    submissionDeadlineDay: Number(policy.submissionDeadlineDay),
    expenseApprovalFlow: policy.expenseApprovalFlow,
    isActive: policy.isActive !== false,
  };
}

function modePayload(mode, policyId) {
  const ownVehicle = isOwnVehicleMode(mode.modeTransport);
  return {
    policyId,
    modeTransport: mode.modeTransport,
    isEnabled: Boolean(mode.isEnabled),
    ratePerKm: ownVehicle ? decimal(mode.ratePerKm) : null,
    isAtActuals: !ownVehicle,
  };
}

/** The tenant's Local Conveyance policy, or null on first-time setup. */
export async function fetchPolicy() {
  const response = await hrmsGet(
    "/local-expense-policies/search?page=0&size=10",
    CODES.SEARCH_POLICY
  );
  // `data` is the page object when the endpoint paginates, the array when it does not.
  const rows = response?.data?.content ?? response?.data ?? [];
  const conveyance = rows.filter(
    (row) => !row.category || row.category === EXPENSE_CATEGORY
  );
  return conveyance.find((row) => row.isActive) ?? conveyance[0] ?? null;
}

/** Mode rows belonging to one policy. */
export async function fetchModes(policyId) {
  if (!policyId) return [];
  const response = await hrmsGet(
    "/local-expense-policy-modes/search?page=0&size=10",
    CODES.SEARCH_MODE
  );
  const rows = response?.data?.content ?? response?.data ?? [];
  return rows.filter((row) => String(row.policyId) === String(policyId));
}

/**
 * Writes the header, then upserts each mode row.
 *
 * Order matters: on a first-time save there is no policy_id yet, and
 * org_expense_policy_mode.policy_id is a NOT NULL FK, so the mode rows cannot
 * be written until the header returns its generated id. Rows are sent one at a
 * time because the API exposes no bulk endpoint.
 */
export async function savePolicy({ policy, modes }) {
  const body = policyPayload(policy);

  const saved = policy.policyId
    ? await hrmsPut(`/local-expense-policies/update/${policy.policyId}`, body, CODES.UPDATE_POLICY)
    : await hrmsPost("/local-expense-policies/add", body, CODES.ADD_POLICY);

  const savedPolicy = saved.data;
  const policyId = savedPolicy.policyId;

  const savedModes = [];
  for (const mode of modes) {
    const payload = modePayload(mode, policyId);
    const response = mode.modeId
      ? await hrmsPut(
          `/local-expense-policy-modes/update/${mode.modeId}`,
          payload,
          CODES.UPDATE_MODE
        )
      : await hrmsPost("/local-expense-policy-modes/add", payload, CODES.ADD_MODE);
    savedModes.push(response.data);
  }

  return { policy: savedPolicy, modes: savedModes };
}

export const expensePolicyService = { fetchPolicy, fetchModes, savePolicy };

export default expensePolicyService;
